import { useState } from 'react'
import { Link, useNavigate, useParams } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, ChevronRight } from 'lucide-react'
import type { CiDryRun, CiJob, CiRun, CiRunDetail, CiRunList, CiRunStarted, Project } from '@one-deployment/api'
import { Badge, Button, Card, PageHeader, SkeletonRows, Table, Td, Tr } from '@/components/ui'
import { Age, EmptyState, ErrorState } from '@/components/status'
import { RoleGate } from '@/components/RoleGate'
import { askText } from '@/components/ask'
import { ApiError, enc, get, post } from '@/lib/api'
import { useLiveQueryOpts, useLiveTopic } from '@/lib/live'
import { qk } from '@/lib/query'
import { useProject } from '@/features/projects/data'
import { PipelineGraph } from './Pipeline'

/**
 * CI (features/21): the project's runs from .trisof/ci.yml, a run's jobs and
 * their logs. `ci.updated` on the project topic refetches runs; a log polls
 * every 3 s while its job runs, since a refetch per `ci.log` chunk would be
 * one request per line batch.
 */

type Tone = 'muted' | 'ok' | 'warn' | 'err' | 'info'
const TONE: Record<string, Tone> = {
  queued: 'muted', waiting: 'muted', running: 'info', leased: 'info', success: 'ok', failure: 'err', error: 'err', cancelled: 'warn', skipped: 'muted',
}
/** `leased` is the database's word; people see "running". */
const LABEL: Record<string, string> = { leased: 'running' }
export const LIVE = new Set(['queued', 'running', 'waiting', 'leased'])

function StatusPill({ status }: { status: string }) {
  return <Badge tone={TONE[status] ?? 'muted'}>{LABEL[status] ?? status}</Badge>
}

export function duration(from: number | null, to: number | null, now = Date.now()): string {
  if (!from) return '—'
  const s = Math.max(0, Math.round(((to ?? now) - from) / 1000))
  return s < 60 ? `${s}s` : s < 3600 ? `${Math.floor(s / 60)}m ${s % 60}s` : `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`
}

const commitUrl = (p: Project | undefined, sha: string) => (p ? `https://github.com/${p.repo.owner}/${p.repo.name}/commit/${sha}` : undefined)
const eventLabel = (r: CiRun) => (r.event === 'pull_request' ? `PR #${r.prNumber}` : r.event)

function useCiRuns(project: string) {
  const live = useLiveQueryOpts(`project:${project}`)
  return useQuery({ queryKey: qk.ciRuns(project), queryFn: ({ signal }) => get<CiRunList>(`/api/projects/${enc(project)}/ci/runs?limit=100`, signal), ...live })
}

export function useCiRun(project: string, runId: string, enabled = true) {
  const live = useLiveQueryOpts(`project:${project}`)
  return useQuery({
    enabled,
    queryKey: qk.ciRun(project, runId),
    queryFn: ({ signal }) => get<CiRunDetail>(`/api/projects/${enc(project)}/ci/runs/${enc(runId)}`, signal),
    ...live,
    // A missed event must not leave a running page frozen.
    refetchInterval: (q) => (q.state.data && LIVE.has(q.state.data.run.status) ? 10_000 : live.refetchInterval),
  })
}

export function CiRunsPage() {
  const { project } = useParams({ strict: false }) as { project: string }
  useLiveTopic(`project:${project}`)
  const proj = useProject(project)
  const runs = useCiRuns(project)
  const nav = useNavigate()
  const qc = useQueryClient()
  const start = useMutation({
    mutationFn: (v: { branch: string; schedule?: string }) => post<CiRunStarted>(`/api/projects/${enc(project)}/ci/runs`, v),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: qk.ciRuns(project) }); void nav({ to: '/p/$project/ci/$runId', params: { project, runId: r.runId } }) },
  })
  const runOnBranch = async () => {
    const branch = await askText({ title: 'Run CI on a branch', body: 'Runs .trisof/ci.yml at the branch\'s latest commit.', confirm: 'Run', value: proj.data?.targetBranch ?? proj.data?.repo.defaultBranch ?? 'main' })
    if (branch) start.mutate({ branch })
  }
  // Dry run: what a run would do, started nowhere; the Worker reads the pipeline, so only a browser is needed.
  const [dry, setDry] = useState<CiDryRun | null>(null)
  const dryRun = useMutation({
    mutationFn: (branch: string) => get<CiDryRun>(`/api/projects/${enc(project)}/ci/dry-run?branch=${encodeURIComponent(branch)}`),
    onSuccess: setDry,
  })
  const dryOnBranch = async () => {
    const branch = await askText({ title: 'Dry run on a branch', body: 'Shows the jobs, steps and secrets a run would use at the branch\'s latest commit. Nothing runs.', confirm: 'Show', value: proj.data?.targetBranch ?? proj.data?.repo.defaultBranch ?? 'main' })
    if (branch) dryRun.mutate(branch)
  }
  // A schedule's jobs now (backup, patch, weekly images), on the default branch.
  const runSchedule = async () => {
    const schedule = await askText({ title: 'Run a schedule now', body: 'Runs the jobs of one on.schedule entry in .trisof/ci.yml, at the default branch\'s latest commit.', confirm: 'Run', value: '' })
    if (schedule) start.mutate({ branch: proj.data?.repo.defaultBranch ?? 'main', schedule: schedule.trim() })
  }

  return (
    <div>
      <PageHeader title="CI" meta={<>{proj.data?.name ?? project} · runs from .trisof/project.yml or ci.yml</>} actions={
        <RoleGate action="ci.run" project={project}>
          <Button disabled={dryRun.isPending} onClick={() => void dryOnBranch()}>Dry run</Button>
          <Button disabled={start.isPending} onClick={() => void runSchedule()}>Run a schedule</Button>
          <Button variant="primary" disabled={start.isPending} onClick={() => void runOnBranch()}>Run on branch</Button>
        </RoleGate>
      } />
      <div className="space-y-3 px-6 py-5">
        {start.error && <ErrorState error={start.error} compact />}
        {dryRun.error && <ErrorState error={dryRun.error} compact />}
        {dry && <DryRunCard dry={dry} onClose={() => setDry(null)} />}
        {runs.error && !runs.data ? <ErrorState error={runs.error} retry={() => void runs.refetch()} /> : !runs.data ? <SkeletonRows rows={8} /> : runs.data.runs.length === 0 ? (
          <EmptyState title="No runs yet" body="A push or pull request with .trisof/ci.yml starts one. Or run it on a branch now." />
        ) : (
          <Card>
            <Table head={['Status', 'Branch', 'Commit', 'Event', 'Started', 'Took']}>
              {runs.data.runs.map((r) => (
                <Tr key={r.id} onClick={() => void nav({ to: '/p/$project/ci/$runId', params: { project, runId: r.id } })}>
                  <Td><StatusPill status={r.status} /></Td>
                  <Td className="max-w-64 truncate">{r.branch}</Td>
                  <Td mono><a href={commitUrl(proj.data, r.sha)} target="_blank" rel="noopener" onClick={(e) => e.stopPropagation()} className="text-accent-hover">{r.sha.slice(0, 7)}</a></Td>
                  <Td className="text-fg-muted">{eventLabel(r)}</Td>
                  <Td className="text-fg-muted"><Age from={r.createdAt} /></Td>
                  <Td className="text-fg-muted tabular-nums">{duration(r.createdAt, r.finishedAt)}</Td>
                </Tr>
              ))}
            </Table>
          </Card>
        )}
      </div>
    </div>
  )
}

function DryRunCard({ dry, onClose }: { dry: CiDryRun; onClose: () => void }) {
  const tone = { run: 'ok', skip: 'muted', error: 'err' } as const
  return (
    <Card>
      <div className="flex items-center justify-between border-b border-border px-4 py-2 text-sm">
        <span>Dry run at <span className="font-mono">{dry.sha.slice(0, 7)}</span>. Nothing was started.</span>
        <Button onClick={onClose}>Close</Button>
      </div>
      {dry.error ? <div className="px-4 py-3 text-sm text-err">{dry.error}</div> : (
        <Table head={['Job', 'Will', 'Needs', 'Secrets', 'Steps']}>
          {dry.jobs.map((j) => (
            <Tr key={j.name}>
              <Td mono>{j.name}</Td>
              <Td><Badge tone={tone[j.will]}>{j.will}</Badge>{j.why && <div className="text-xs text-fg-muted">{j.why}</div>}</Td>
              <Td className="text-fg-muted">{j.needs.join(', ') || '—'}</Td>
              <Td mono className="text-fg-muted">{j.secrets.join(', ') || '—'}</Td>
              <Td><ol className="list-decimal pl-4 text-xs">{j.steps.map((s, i) => <li key={i} className="font-mono">{s.run}</li>)}</ol></Td>
            </Tr>
          ))}
        </Table>
      )}
    </Card>
  )
}

export function CiRunPage() {
  const { project, runId } = useParams({ strict: false }) as { project: string; runId: string }
  useLiveTopic(`project:${project}`)
  const proj = useProject(project)
  const q = useCiRun(project, runId)
  const qc = useQueryClient()
  const nav = useNavigate()
  const cancel = useMutation({
    mutationFn: () => post<CiRunDetail>(`/api/projects/${enc(project)}/ci/runs/${enc(runId)}/cancel`),
    onSuccess: (d) => { qc.setQueryData(qk.ciRun(project, runId), d); void qc.invalidateQueries({ queryKey: qk.ciRuns(project) }) },
  })
  const rerun = useMutation({
    mutationFn: () => post<CiRunStarted>(`/api/projects/${enc(project)}/ci/runs/${enc(runId)}/rerun`),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: qk.ciRuns(project) }); void nav({ to: '/p/$project/ci/$runId', params: { project, runId: r.runId } }) },
  })

  if (q.error && !q.data) return <ErrorState error={q.error} retry={() => void q.refetch()} />
  if (!q.data) return <div className="p-5"><SkeletonRows rows={6} /></div>
  const { run, jobs } = q.data
  const live = LIVE.has(run.status)
  return (
    <div>
      <PageHeader
        title={<span className="flex items-center gap-2">{run.branch} <StatusPill status={run.status} /></span>}
        meta={<>
          <a href={commitUrl(proj.data, run.sha)} target="_blank" rel="noopener" className="font-mono text-accent-hover hover:underline">{run.sha.slice(0, 7)}</a>
          {' · '}{eventLabel(run)} · <Age from={run.createdAt} /> ago · took {duration(run.createdAt, run.finishedAt)}
          {' · '}<Link to="/p/$project/ci" params={{ project }} className="hover:text-fg">all runs</Link>
          {q.data.newer && <>{' · '}<Link to="/p/$project/ci/$runId" params={{ project, runId: q.data.newer }} className="text-accent hover:underline">re-run of this commit: open the latest</Link></>}
        </>}
        actions={
          <RoleGate action="ci.run" project={project}>
            {live && <Button disabled={cancel.isPending} onClick={() => cancel.mutate()}>Cancel</Button>}
            <Button disabled={rerun.isPending} onClick={() => rerun.mutate()}>Re-run</Button>
          </RoleGate>
        }
      />
      <div className="space-y-3 px-6 py-5">
        {(cancel.error || rerun.error) && <ErrorState error={cancel.error ?? rerun.error} compact />}
        {run.error && <Card><pre className="p-3 font-mono text-xs whitespace-pre-wrap text-err">{run.error}</pre></Card>}
        {jobs.length > 1 && <Card className="p-4"><PipelineGraph nodes={jobs} /></Card>}
        {jobs.length > 0 && (
          <Card>
            {jobs.map((j) => <JobRow key={j.id} project={project} job={j} />)}
          </Card>
        )}
      </div>
    </div>
  )
}

/** One job; its log opens below it. Jobs come in the file's order, so `needs` always point up. */
function JobRow({ project, job }: { project: string; job: CiJob }) {
  const [open, setOpen] = useState(job.status === 'failure' || job.status === 'error')
  return (
    <div className="border-b border-border last:border-b-0">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open} className="flex h-9 w-full items-center gap-2 px-3 text-left text-sm outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 hover:bg-hover">
        {open ? <ChevronDown size={14} className="text-fg-subtle" /> : <ChevronRight size={14} className="text-fg-subtle" />}
        <StatusPill status={job.status} />
        <span className="font-medium">{job.name}</span>
        {job.needs.length > 0 && <span className="text-xs text-fg-faint">after {job.needs.join(', ')}</span>}
        <span className="flex-1 truncate text-xs text-fg-subtle">{job.error}</span>
        {job.attempts > 1 && <span className="text-2xs text-fg-faint">attempt {job.attempts}</span>}
        <span className="text-xs text-fg-muted tabular-nums">{duration(job.startedAt, job.finishedAt)}</span>
      </button>
      {open && <JobLog project={project} job={job} />}
    </div>
  )
}

function JobLog({ project, job }: { project: string; job: CiJob }) {
  const running = job.status === 'leased'
  const log = useQuery({
    queryKey: [...qk.ciLog(project, job.id), job.status],
    queryFn: ({ signal }) => logText(`/api/projects/${enc(project)}/ci/jobs/${enc(job.id)}/log`, signal),
    refetchInterval: running ? 3_000 : false,
    staleTime: running ? 0 : Infinity,
  })
  if (job.status === 'waiting' || job.status === 'queued') return <div className="px-9 pb-3 text-xs text-fg-faint">Not started yet.</div>
  if (log.error) return <div className="px-3 pb-3"><ErrorState error={log.error} compact /></div>
  if (log.data === undefined) return <div className="px-3 pb-3"><SkeletonRows rows={3} height={16} /></div>
  return (
    <pre className="mx-3 mb-3 max-h-[60vh] overflow-auto rounded-sm bg-surface-1 p-3 font-mono text-xs whitespace-pre-wrap">{log.data || 'No output.'}</pre>
  )
}

/** The log is text/plain, which `api()` would read as an error body. */
async function logText(path: string, signal?: AbortSignal): Promise<string> {
  const res = await fetch(path, { credentials: 'same-origin', signal })
  const text = await res.text()
  if (!res.ok) {
    let msg = text
    try { msg = (JSON.parse(text) as { error: { message: string } }).error.message } catch { /* plain text */ }
    throw new ApiError(res.status, `http_${res.status}`, msg, msg)
  }
  return text
}
